import { SlideshowSelectEvent } from '@theme/events';

class NooshVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[ref="track"]');

    // Opt-in: appends a clone of every real slide after the last one and
    // prepends a clone of every real slide before the first one, so
    // advancing past either end scrolls into a pixel-identical clone
    // instead of jumping back across the whole track (or leaving the
    // track empty for a frame). Must run before any of the
    // querySelector('[data-video-mute]') etc. wiring below so the clones'
    // own buttons/videos get the same listeners.
    this.infiniteLoop = this.hasAttribute('data-infinite-loop');
    if (this.infiniteLoop) this.setupInfiniteLoopClones();

    this.nextButton = this.querySelector('[data-carousel-next]');
    this.prevButton = this.querySelector('[data-carousel-prev]');

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scrollToOffset(1));
    }
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.scrollToOffset(-1));
    }

    this.querySelectorAll('[data-video-mute]').forEach((button) => {
      button.addEventListener('click', () => this.toggleMute(button));
    });

    this.querySelectorAll('[data-video-toggle]').forEach((button) => {
      button.addEventListener('click', () => this.togglePlay(button));
    });

    this.querySelectorAll('video').forEach((video) => {
      const button = video.parentElement?.querySelector('[data-video-toggle]');
      if (button) {
        video.addEventListener('play', () => button.setAttribute('data-playing', ''));
        video.addEventListener('pause', () => button.removeAttribute('data-playing'));
      }

      const progressBar = video.parentElement?.querySelector('[data-video-progress]');
      if (progressBar) {
        video.addEventListener('timeupdate', () => {
          if (!video.duration) return;
          progressBar.style.transform = `scaleX(${video.currentTime / video.duration})`;
        });
      }
    });

    // "start": the active slide sits flush at the track's left edge, with
    // later slides peeking on the right (hero slider). "center": the
    // active slide is whichever one sits in the middle of the visible
    // strip, with a neighbor visible on each side (media row).
    this.align = this.dataset.align === 'center' ? 'center' : 'start';

    // Only the video "story" carousels (hero, media row) want inactive
    // slides dimmed/scaled down — plain link carousels (e.g. collection
    // cards) reuse this same element just for its scroll/arrow behavior
    // and should be left at full opacity.
    this.dimInactive = this.hasAttribute('data-dim-inactive');

    this.activeIndex = 0;
    this.setupActiveVideoTracking();
    this.setupManualScrollTracking();

    const slides = this.slides;
    if (slides.length) {
      const initialIndex = Number.parseInt(this.dataset.initialIndex ?? '0', 10) || 0;
      const initialSlide = slides[initialIndex] ?? slides[0];
      this.activateSlide(initialSlide);
      this.scrollToSlide(initialSlide);
    }
  }

  get slides() {
    if (!this.track) return [];
    return Array.from(this.track.children).filter((el) => !el.dataset.clone);
  }

  // Clones every real slide once before the first and once after the last,
  // marked with data-clone so get slides() (and everything built on it)
  // keeps seeing only the real ones. loopCloneBefore/After map a real
  // slide's index to its clone on that side, used by scrollToOffset() as
  // the physical scroll destination when wrapping.
  setupInfiniteLoopClones() {
    if (!this.track) return;
    const realSlides = Array.from(this.track.children);
    if (realSlides.length < 2) return;

    const cloneSlide = (slide) => {
      const clone = slide.cloneNode(true);
      clone.dataset.clone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.remove('is-active');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.muted = true;
        video.preload = 'none';
        video.pause();
      });
      return clone;
    };

    this.loopCloneBefore = realSlides.map(cloneSlide);
    this.loopCloneAfter = realSlides.map(cloneSlide);

    const beforeFragment = document.createDocumentFragment();
    this.loopCloneBefore.forEach((clone) => beforeFragment.appendChild(clone));
    this.track.insertBefore(beforeFragment, this.track.firstChild);

    const afterFragment = document.createDocumentFragment();
    this.loopCloneAfter.forEach((clone) => afterFragment.appendChild(clone));
    this.track.appendChild(afterFragment);
  }

  // direction: 1 for next, -1 for prev. Wraps around at either end.
  scrollToOffset(direction) {
    if (!this.track) return;
    const slides = this.slides;
    if (slides.length < 2) return;

    // Use the tracked active index rather than re-deriving it from
    // getBoundingClientRect() positions: at the last slide the browser
    // often can't scroll far enough to align it exactly with the snap
    // point (there's no more room), so the geometry-based lookup could
    // resolve to the wrong slide there — which broke wrapping back to the
    // first slide and left the "active" styling out of sync with which
    // video was actually playing.
    const baseIndex = this.activeIndex ?? 0;
    const rawIndex = baseIndex + direction;
    const wrapped = rawIndex < 0 || rawIndex >= slides.length;
    const targetIndex = (rawIndex + slides.length) % slides.length;
    const target = slides[targetIndex];
    if (!target) return;

    // For center-aligned carousels, activateSlide() re-flows the slides
    // (via flex `order`) so the active one always sits in the middle slot,
    // wrapping neighbors around it — that has to happen *before* we measure
    // where to scroll, otherwise scrollToSlide() targets the pre-reorder
    // layout and the centering is one step behind.
    this.activateSlide(target);

    // When wrapping, scroll to the clone that physically continues past the
    // end/start instead of jumping straight to the real target (which would
    // otherwise be a visible backward leap across the whole track). Once
    // that scroll settles, snap (no animation) onto the real slide's
    // position — invisible since the clone is pixel-identical.
    let scrollDestination = target;
    if (this.infiniteLoop && wrapped) {
      scrollDestination = (direction > 0 ? this.loopCloneAfter : this.loopCloneBefore)?.[targetIndex] ?? target;
    }
    this.scrollToSlide(scrollDestination, 'smooth');

    if (this.infiniteLoop && wrapped) {
      window.clearTimeout(this.loopSnapTimeout);
      this.loopSnapTimeout = window.setTimeout(() => {
        this.suppressScrollTracking = true;
        this.track.scrollTo({ left: this.getSlideLeft(target), behavior: 'auto' });
        window.setTimeout(() => {
          this.suppressScrollTracking = false;
        }, 50);
      }, 500);
    }

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: targetIndex,
        previousIndex: baseIndex,
        userInitiated: true,
        trigger: 'select',
        slide: target,
        id: target.dataset.slideId ?? null,
      })
    );
  }

  getSlideLeft(slide) {
    return this.align === 'center'
      ? slide.offsetLeft - this.track.clientWidth / 2 + slide.offsetWidth / 2
      : slide.offsetLeft - this.track.offsetLeft;
  }

  scrollToSlide(slide, behavior = 'auto') {
    if (!this.track) return;
    const left = this.getSlideLeft(slide);

    if (behavior === 'smooth') {
      // A JS-driven smooth scroll on a `scroll-snap-type: mandatory` track
      // can end with the browser's own snap logic nudging scrollLeft one
      // slide further than the position we asked for (a known scroll-snap
      // + scrollTo interaction quirk). setupManualScrollTracking() would
      // then "confirm" that overshoot as the new active slide 150ms later,
      // which reads as clicking next from slide 2 skipping straight to
      // slide 3. Button-driven navigation already knows exactly which
      // slide it's activating (see scrollToOffset), so suppress the
      // geometry-based re-detection for the duration of this animation
      // instead of letting it second-guess the explicit activateSlide().
      this.suppressScrollTracking = true;
      window.clearTimeout(this.suppressScrollTrackingTimeout);
      this.suppressScrollTrackingTimeout = window.setTimeout(() => {
        this.suppressScrollTracking = false;
      }, 500);
    }

    this.track.scrollTo({ left, behavior });
  }

  activateSlide(slide) {
    const slides = this.slides;
    const activeIndex = slides.indexOf(slide);
    this.activeIndex = activeIndex === -1 ? 0 : activeIndex;

    // Center-aligned carousels (media row) always keep the active/playing
    // slide in the middle visual slot: with a small, fixed set of slides
    // there often isn't room on one side to scroll a first/last slide into
    // true center (scrollToSlide() would just clamp at the edge), so
    // position is driven by flex `order` instead — reassigned every
    // activation so slides wrap circularly around whichever one is active,
    // the way Jurni's carousel does, rather than pinning to an edge.
    if (this.align === 'center' && this.activeIndex !== -1) {
      const total = slides.length;
      const middle = Math.floor(total / 2);
      slides.forEach((other, index) => {
        let rel = ((index - this.activeIndex) % total + total) % total;
        if (rel > total / 2) rel -= total;
        other.style.order = String(middle + rel);
      });
    }

    slides.forEach((other, index) => {
      const video = other.querySelector('video');
      if (other === slide) {
        other.classList.add('is-active');
        if (this.dimInactive) other.style.opacity = '';
        this.ensureLoaded(video);
        video?.play?.().catch(() => {});
      } else {
        other.classList.remove('is-active');
        if (this.dimInactive) {
          // Graduated falloff: closer neighbors stay more visible than
          // farther-off slides, rather than a single flat "inactive" look.
          const distance = activeIndex === -1 ? 1 : Math.abs(index - activeIndex);
          other.style.opacity = distance <= 1 ? '0.5' : '0.4';
        }
        video?.pause?.();
      }
    });
  }

  togglePlay(button) {
    const video = button.parentElement?.querySelector('video');
    if (!video) return;
    if (video.paused) {
      this.ensureLoaded(video);
      video.play?.().catch(() => {});
    } else {
      video.pause?.();
    }
  }

  // Non-initial slides render with preload="none" so the browser doesn't
  // start downloading every video on the page at once — only the video
  // that's actually about to play should fetch its data. Bumping preload
  // to "auto" is the standard way to trigger that fetch on demand.
  ensureLoaded(video) {
    if (video && video.preload !== 'auto') {
      video.preload = 'auto';
    }
  }

  toggleMute(button) {
    const video = button.previousElementSibling;
    if (!video || video.tagName !== 'VIDEO') return;
    video.muted = !video.muted;
    button.setAttribute('aria-pressed', String(!video.muted));
  }

  setupActiveVideoTracking() {
    const videos = Array.from(this.querySelectorAll('video'));
    if (!videos.length) return;

    // Playing many autoplaying videos at once causes browsers to throttle
    // decoding of the off-screen ones, which reads as a blurry/frozen slide.
    // Only keep the slide(s) actually in view playing.
    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            // In layouts where several slides are simultaneously well
            // past this threshold (e.g. media row's 3-up center layout,
            // where the two side cards are also mostly visible), only
            // resume the slide the carousel has actually marked active —
            // otherwise this would auto-play side cards right back after
            // activateSlide() explicitly paused them.
            const slide = video.parentElement;
            if (!this.dimInactive || slide?.classList.contains('is-active')) {
              this.ensureLoaded(video);
              video.play?.().catch(() => {});
            }
          } else {
            video.pause?.();
          }
        });
      },
      { root: this.track || null, threshold: [0, 0.6, 1] }
    );

    videos.forEach((video) => this.videoObserver.observe(video));
  }

  // Covers touch/drag scrolling of the track, which doesn't go through
  // scrollToOffset(). Debounced so it only fires once scrolling settles, then
  // explicitly activates whichever slide ended up closest to the start of
  // the visible area (same determinism as the arrow-button path).
  setupManualScrollTracking() {
    if (!this.track) return;

    this.track.addEventListener('scroll', () => {
      if (this.suppressScrollTracking) return;

      window.clearTimeout(this.scrollSettleTimeout);
      this.scrollSettleTimeout = window.setTimeout(() => {
        if (this.suppressScrollTracking) return;

        const slides = this.slides;
        if (!slides.length) return;

        // At either end of the track the browser can't scroll any further
        // even if the last/first slide isn't fully snapped into its target
        // position (there's no more room) — the same issue scrollToOffset()
        // works around below. Check scroll position against the track's
        // actual max scroll first, and only fall back to geometry for
        // slides in the middle. Skipped for infinite-loop carousels: there
        // the absolute start/end of the track sit inside the prepended/
        // appended clones, not the real first/last slide, so this shortcut
        // would report the wrong slide — geometry-based lookup below still
        // resolves correctly since it measures the real slides directly.
        if (!this.infiniteLoop) {
          const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;
          if (maxScrollLeft > 0 && this.track.scrollLeft >= maxScrollLeft - 1) {
            this.activateSlideFromScroll(slides[slides.length - 1]);
            return;
          }
          if (this.track.scrollLeft <= 1) {
            this.activateSlideFromScroll(slides[0]);
            return;
          }
        }

        const trackRect = this.track.getBoundingClientRect();
        const targetX = this.align === 'center' ? trackRect.left + trackRect.width / 2 : trackRect.left;
        const closest = slides.reduce((best, slide) => {
          const rect = slide.getBoundingClientRect();
          const slideX = this.align === 'center' ? rect.left + rect.width / 2 : rect.left;
          const distance = Math.abs(slideX - targetX);
          return distance < best.distance ? { slide, distance } : best;
        }, { slide: slides[0], distance: Infinity });

        this.activateSlideFromScroll(closest.slide);
      }, 150);
    });
  }

  // Activates a slide reached via touch/drag/momentum scroll and, if that
  // actually changed the active slide, reports it the same way the theme's
  // native slideshow component does — so swipe-driven changes show up in
  // tracking alongside the button-driven ones from scrollToOffset().
  activateSlideFromScroll(slide) {
    const previousIndex = this.activeIndex ?? 0;
    this.activateSlide(slide);

    // A manual drag/swipe can carry the scroll position past the real slide
    // and onto one of its clones (e.g. flinging past the last slide lands on
    // the appended clone right after it). Snap the real scroll position to
    // match without animation — invisible since the clone is pixel-identical
    // to the real slide `slide` now refers to.
    if (this.infiniteLoop) {
      const expectedLeft = this.getSlideLeft(slide);
      if (Math.abs(this.track.scrollLeft - expectedLeft) > 2) {
        this.suppressScrollTracking = true;
        this.track.scrollTo({ left: expectedLeft, behavior: 'auto' });
        window.setTimeout(() => {
          this.suppressScrollTracking = false;
        }, 50);
      }
    }

    if (this.activeIndex === previousIndex) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: this.activeIndex,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.dataset.slideId ?? null,
      })
    );
  }
}

if (!customElements.get('noosh-video-carousel')) {
  customElements.define('noosh-video-carousel', NooshVideoCarousel);
}
